import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { ApiError } from '../../api/client';
import type { Session } from '../../api/session';
import {
  closeConfidance, createConfidance, createConfidanceRequest, getConfidances, getObject, getObjectAddresses,
  type NewConfidance, type ObjectInfo,
} from '../../api/tenant';
import { endOfMoscowDay, formatServerDate, moscowShortDate, parseServerDate } from '../../domain/moscowTime';
import { describePassError } from '../../domain/passErrors';
import { CONFIDANCE_STATUS, confidanceDurations, formatPhone, nameError, normalizePhone } from '../../domain/people';
import type { TelegramApi } from '../../telegram/webapp';
import { Chips } from '../../ui/Choice';
import { Skeleton } from '../../ui/Skeleton';
import c from '../pass-card/passCard.module.css';
import f from '../pass-form/passForm.module.css';
import p from '../passes/passes.module.css';
import s from './more.module.css';

const ACTIVE = new Set([1, 2, 4]);

function statusLabel(status: number, startsAt: string) {
  if (status === 4) {
    const at = parseServerDate(startsAt);
    return at ? `Начнётся ${moscowShortDate(at)}` : CONFIDANCE_STATUS[4];
  }
  return CONFIDANCE_STATUS[status] ?? '—';
}

/** Powers of attorney issued by the resident: list, close, issue (spec §6.8). */
export function ConfidancesScreen({ session, telegram, object }: { session: Session; telegram: TelegramApi; object: ObjectInfo }) {
  const queryClient = useQueryClient();
  const list = useQuery({ queryKey: ['confidances'], queryFn: () => getConfidances(session) });
  const details = useQuery({ queryKey: ['object', object.id], queryFn: () => getObject(session, object.id) });
  const addresses = useQuery({ queryKey: ['addresses', object.id], queryFn: () => getObjectAddresses(session, object.id) });
  const own = useMemo(() => (addresses.data ?? []).filter((a) => !a.isBlocked && a.confidanceId === 0), [addresses.data]);
  const settings = details.data?.settings ?? {};
  const viaRequest = !!settings.confidencesEnableFromRequests && settings.enableRequestsForConfidences !== false;
  const maxHours = typeof settings.confidancesMaxDuration === 'number' ? settings.confidancesMaxDuration : null;
  const durations = confidanceDurations(maxHours);

  const [closingId, setClosingId] = useState<number | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [addressId, setAddressId] = useState<number | null>(null);
  const [phone, setPhone] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [days, setDays] = useState<number>(1);
  const [forceRequest, setForceRequest] = useState(false);
  const [errors, setErrors] = useState<{ phone?: string; firstName?: string; lastName?: string; general?: string }>({});
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    if (addressId === null && own.length) setAddressId(own[0].id);
  }, [own, addressId]);

  const close = useMutation({
    mutationFn: (id: number) => closeConfidance(session, id),
    onSuccess: () => {
      setClosingId(null);
      void queryClient.invalidateQueries({ queryKey: ['confidances'] });
    },
  });

  const request = viaRequest || forceRequest;
  const issue = useMutation({
    mutationFn: (): Promise<unknown> => {
      const now = new Date();
      const body: NewConfidance = {
        addressId: addressId!,
        startsAt: formatServerDate(now),
        expiresAt: formatServerDate(endOfMoscowDay(now, days - 1)),
        confidant: { phone: normalizePhone(phone)!, firstName: firstName.trim(), lastName: lastName.trim() || null, middleName: null },
      };
      return request ? createConfidanceRequest(session, body) : createConfidance(session, body);
    },
    onSuccess: () => {
      telegram.notify('success');
      setDone(request ? 'Запрос отправлен в УК — статус в «Обращениях».' : 'Доверенность выдана.');
      setFormOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['confidances'] });
    },
    onError: (e) => {
      telegram.notify('error');
      if (e instanceof ApiError && e.code === 'CONFIDENCES_ENABLE_FROM_REQUESTS') {
        setForceRequest(true);
        return setErrors({ general: 'На этом объекте доверенность выдаётся через УК' });
      }
      if (e instanceof ApiError && e.code === 'CONFIDANCES_QUOTA_EXCEEDED') return setErrors({ general: 'Лимит доверенностей исчерпан' });
      if (e instanceof ApiError && e.code === 'VALIDATION_ERROR' && e.details) {
        const d = e.details;
        return setErrors({
          phone: d['confidant.phone']?.[0],
          firstName: d['confidant.firstName']?.[0],
          general: d.expiresAt?.[0] ?? d.startsAt?.[0],
        });
      }
      setErrors({ general: describePassError(e).general ?? 'Не получилось' });
    },
  });

  return (
    <main className={p.page}>
      <h1 className={p.title}>Доверенности</h1>
      <p className={f.where}>Доверенное лицо сможет заказывать пропуска на ваш адрес в течение срока доверенности.</p>
      {done && <p className={p.empty}>{done}</p>}

      {!list.data ? (
        <Skeleton height={100} />
      ) : list.data.length === 0 ? (
        <p className={p.empty}>Вы ещё не выдавали доверенностей</p>
      ) : (
        <ul className={p.list}>
          {list.data.map((x) => (
            <li key={x.id} className={s.row}>
              <span className={s.rowText}>
                <span className={s.rowName}>{[x.confidant.firstName, x.confidant.lastName].filter(Boolean).join(' ')}</span>
                <span className={s.rowSub}>
                  {formatPhone(x.confidant.phone)} · {x.address.name}
                </span>
                <span className={s.mark}>{statusLabel(x.status, x.startsAt)}</span>
              </span>
              {ACTIVE.has(x.status) &&
                (closingId === x.id ? (
                  <button type="button" className={c.danger} onClick={() => close.mutate(x.id)} disabled={close.isPending}>
                    Да, закрыть
                  </button>
                ) : (
                  <button type="button" className={s.small} onClick={() => setClosingId(x.id)}>
                    Закрыть
                  </button>
                ))}
            </li>
          ))}
        </ul>
      )}

      {!formOpen ? (
        <button type="button" className={f.submit} onClick={() => { setDone(null); setFormOpen(true); }} disabled={!own.length}>
          Выдать доверенность
        </button>
      ) : (
        <section className={f.sheet} aria-label="Новая доверенность">
          {own.length > 1 && (
            <Chips label="Адрес" value={addressId} items={own.map((a) => ({ id: a.id, label: a.name }))} onChange={(v) => v !== null && setAddressId(v)} />
          )}
          <div className={f.field}>
            <label htmlFor="c-phone" className={f.label}>
              Телефон доверенного лица
            </label>
            <input id="c-phone" type="tel" inputMode="tel" className={errors.phone ? `${f.input} ${f.invalid}` : f.input} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+7 900 000-00-00" />
            <span className={errors.phone ? f.fieldError : f.hint}>{errors.phone ?? 'Введите вручную: доступа к контактам у приложения нет'}</span>
          </div>
          <div className={f.field}>
            <label htmlFor="c-first" className={f.label}>
              Имя
            </label>
            <input id="c-first" className={errors.firstName ? `${f.input} ${f.invalid}` : f.input} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            {errors.firstName && <span className={f.fieldError}>{errors.firstName}</span>}
          </div>
          <div className={f.field}>
            <label htmlFor="c-last" className={f.label}>
              Фамилия
            </label>
            <input id="c-last" className={f.input} value={lastName} onChange={(e) => setLastName(e.target.value)} />
            {errors.lastName && <span className={f.fieldError}>{errors.lastName}</span>}
          </div>
          <div className={f.field}>
            <span className={f.label}>Срок</span>
            <Chips label="Срок доверенности" value={days} items={durations.map((d) => ({ id: d.days, label: d.label }))} onChange={(v) => v && setDays(v)} />
          </div>
          {errors.general && <p className={f.error} role="alert">{errors.general}</p>}
          <button
            type="button"
            className={f.submit}
            disabled={issue.isPending}
            onClick={() => {
              const next: typeof errors = {};
              if (!normalizePhone(phone)) next.phone = 'Введите номер из 10 цифр, например +7 900 000-00-00';
              const fn = nameError(firstName, true);
              if (fn) next.firstName = fn;
              const ln = nameError(lastName, false);
              if (ln) next.lastName = ln;
              setErrors(next);
              if (!Object.keys(next).length) issue.mutate();
            }}
          >
            {request ? 'Запросить у УК' : 'Выдать'}
          </button>
        </section>
      )}
    </main>
  );
}
